import type { ExcelOperationOwner, ExcelOperationRecord } from "./continuationBindings";

/**
 * One gate for every way a submission can claim to continue an existing
 * operation: a paused-plan retry action or a reply to a pending clarification.
 *
 * The claim is validated as a whole, before Skills resolution, tracing, or any
 * provider work, so a continuation either runs under the operation's frozen
 * context or does not run at all. Checking these one at a time further down the
 * run is what allows a half-valid continuation to start work it cannot finish
 * correctly.
 */

/** What a submission asserts about the operation it is continuing. */
export interface ExcelOperationClaim {
  /** Operation id from the paused-plan payload or the pending clarification. */
  readonly operationId?: string;
  /** Whether the original run resolved to a Skill. */
  readonly skillBound?: boolean;
  /**
   * Checkpoint schema version, for a paused-plan retry only. A clarification
   * reply carries no checkpoint and leaves this `undefined`.
   */
  readonly checkpointVersion?: number;
  /**
   * Account the affordance was published under. Carried by the claim rather
   * than read from the record, because the record is exactly what an account
   * change destroys. `undefined` for a payload written before this field
   * existed, which is left to the record-based checks below.
   */
  readonly accountScopeKey?: string | null;
}

export type ExcelOperationClaimRefusal =
  | "skill_binding_lost"
  | "account_changed"
  | "workbook_changed"
  | "session_changed"
  | "checkpoint_unsupported";

export type ExcelOperationClaimOutcome =
  | { readonly kind: "accepted"; readonly record?: ExcelOperationRecord }
  | {
      readonly kind: "refused";
      readonly reason: ExcelOperationClaimRefusal;
      readonly message: string;
      /**
       * Whether returning to the context the operation belongs to makes this
       * claim valid again. Navigating to another workbook or chat is something
       * the user can undo, so the affordance and its binding must survive; a
       * lost binding, an unsupported checkpoint, or an account change cannot
       * be undone and the affordance should go with them.
       */
      readonly retryable: boolean;
    };

/** The only checkpoint schema this build knows how to resume. */
export const SUPPORTED_EXCEL_CHECKPOINT_VERSION = 1;

const REFUSAL_MESSAGES: Record<ExcelOperationClaimRefusal, string> = {
  skill_binding_lost:
    "This request used a Skill that is no longer available in this session. " +
    "Send the request again to continue with the Skill applied.",
  account_changed:
    "This request was started under a different account. Send it again to continue.",
  workbook_changed:
    "This request was started in a different workbook. Send it again from that workbook to continue.",
  session_changed: "This request belongs to a different chat. Send it again to continue.",
  checkpoint_unsupported: "This continuation is outdated. Send the original request again.",
};

const RETRYABLE_REFUSALS: ReadonlySet<ExcelOperationClaimRefusal> = new Set<ExcelOperationClaimRefusal>(
  ["workbook_changed", "session_changed"]
);

function refuse(reason: ExcelOperationClaimRefusal): ExcelOperationClaimOutcome {
  return {
    kind: "refused",
    reason,
    message: REFUSAL_MESSAGES[reason],
    retryable: RETRYABLE_REFUSALS.has(reason),
  };
}

/**
 * Validates a continuation claim against the frozen operation record.
 *
 * A missing record is only fatal for a Skill-bound claim. An ordinary paused
 * plan predates this registry whenever the task pane reloaded, and refusing it
 * would break resuming work that needs no frozen policy to be correct — the
 * payload carries its own workbook key and checkpoint.
 */
export function validateExcelOperationClaim(params: {
  readonly claim: ExcelOperationClaim;
  /** Owner of the submission asking to continue. */
  readonly owner: ExcelOperationOwner;
  readonly record: ExcelOperationRecord | undefined;
}): ExcelOperationClaimOutcome {
  const { claim, owner, record } = params;
  // Ownership is settled before anything else, and from the claim rather than
  // the record, because an account change is what empties the registry: by the
  // time the record is gone there is nothing left to compare. Checking it
  // first is also what stops a cross-account resume from being reported as a
  // lost Skill or an outdated checkpoint, neither of which is what happened.
  if (
    claim.accountScopeKey !== undefined &&
    claim.accountScopeKey !== owner.accountScopeKey
  ) {
    return refuse("account_changed");
  }
  if (
    claim.checkpointVersion !== undefined &&
    claim.checkpointVersion !== SUPPORTED_EXCEL_CHECKPOINT_VERSION
  ) {
    return refuse("checkpoint_unsupported");
  }
  if (claim.skillBound && !record?.skillContext) {
    return refuse("skill_binding_lost");
  }
  if (!record) return { kind: "accepted" };

  if (record.owner.accountScopeKey !== owner.accountScopeKey) {
    return refuse("account_changed");
  }
  if (record.owner.workbookKey !== owner.workbookKey) {
    return refuse("workbook_changed");
  }
  // The chat session and tab together identify the conversation the operation
  // belongs to. Switching workbooks is navigation and leaves a background run
  // pinned to its own slot, but resuming into a different conversation would
  // apply one request's frozen policy to another's transcript.
  if (record.owner.sessionId !== owner.sessionId || record.owner.tabId !== owner.tabId) {
    return refuse("session_changed");
  }
  return { kind: "accepted", record };
}
